import React, { useState, useEffect } from "react";
import classnames from "classnames";
import PropTypes from "prop-types";
import {
  Collapse,
  Dropdown,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
  DropdownToggle,
  Media,
  Navbar,
  Nav,
  Container,
  NavItem,
} from "reactstrap";
import { useNavigate, useLocation } from "react-router-dom";
import { logout as logoutAction } from "actions/public";
import { dashboardActionRequired } from "actions/dashboard";

function AdminNavbar({ theme, sidenavOpen, toggleSidenav }) {
  const navigate = useNavigate();
  const location = useLocation();
  const userName = JSON.parse(localStorage.getItem("p"));
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // ✅ Notification state
  const [actionRequired, setActionRequired] = useState([]);
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const [isNotifLoading, setIsNotifLoading] = useState(false);

  const isDashboardRoute = location.pathname === "/admin/dashboard";

  // =========================================================
  // ✅ Fetch Action Required — runs on mount + every time
  //    the dropdown is opened (keeps the badge fresh)
  // =========================================================
  const fetchActionRequired = async () => {
    setIsNotifLoading(true);
    try {
      const res = await dashboardActionRequired();
      console.log(res)
      const data = res?.data?.data || res?.data || [];
      setActionRequired(Array.isArray(data) ? data : []);
    } catch (error) {
      console.log("Notification fetch error ===>", error);
      setActionRequired([]);
    } finally {
      setIsNotifLoading(false);
    }
  };

  useEffect(() => {
    if (!isDashboardRoute) return;
    fetchActionRequired();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDashboardRoute]);

  const toggleNotif = () => {
    setIsNotifOpen((prev) => {
      const next = !prev;
      if (next) fetchActionRequired(); // ✅ refetch on open
      return next;
    });
  };

  // ✅ Navigate in same tab + close dropdown
  const goToActionRequired = () => {
    setIsNotifOpen(false);
    navigate("/admin/action-required");
  };

  const logout = async () => {
    try {
      setIsLoggingOut(true);
      const response = await logoutAction();
      if (response?.status === "SUCCESS") {
        localStorage.clear();
        navigate("/auth/login");
      } else {
        localStorage.clear();
        navigate("/auth/login");
      }
    } catch (error) {
      localStorage.clear();
      navigate("/auth/login");
    } finally {
      setIsLoggingOut(false);
    }
  };

  const organizationName = JSON.parse(localStorage.getItem("p"));
  const capitalized = organizationName?.organizationName
    ? organizationName.organizationName.charAt(0).toUpperCase() +
      organizationName.organizationName.slice(1)
    : "";

  // ✅ Total = sum of every item's count
  const totalCount = actionRequired.reduce(
    (sum, item) => sum + (Number(item.count) || 0),
    0,
  );

  return (
    <>
      {isLoggingOut && (
        <div className="logout-overlay">
          <div className="logout-card">
            <i className="fa fa-spinner fa-spin logout-spinner" />
            <h3 className="logout-text">Logging Out...</h3>
          </div>
        </div>
      )}

      <Navbar
        className={classnames(
          "navbar-top navbar-expand border-bottom navbar-dark",
        )}
      >
        <Container fluid>
          <span className="text-white h3 d-none d-sm-block">
            {capitalized}
          </span>

          <Collapse navbar isOpen={true} style={{ justifyContent: "flex-end" }}>
            <Nav className="align-items-center ml-md-auto" navbar>
              <NavItem className="d-xl-none">
                <div
                  className={classnames(
                    "pr-3 sidenav-toggler mt--2",
                    { active: sidenavOpen },
                    { "sidenav-toggler-dark": theme === "dark" },
                  )}
                  onClick={toggleSidenav}
                >
                  <div className="sidenav-toggler-inner">
                    <i className="sidenav-toggler-line-white" />
                    <i className="sidenav-toggler-line-white" />
                    <i className="sidenav-toggler-line-white" />
                  </div>
                </div>
              </NavItem>
              <span className="text-white h3 d-xl-none">{capitalized}</span>
            </Nav>

            <Nav className="align-items-center ml-auto ml-md-0" navbar>
              {/* ✅ Notification Bell — only on /admin/dashboard */}
              {isDashboardRoute && (
                <Dropdown
                  nav
                  isOpen={isNotifOpen}
                  toggle={toggleNotif}
                  inNavbar
                
                >
                  <DropdownToggle
                    nav
                    caret={false}
                    tag="div"
                    className="notification-bell-toggle"
                  >
                    <div className="notification-bell">
                      <i
                        className="fa fa-bell"
                        style={{ color: "#ffffff" }}
                      />
                      {/* {totalCount > 0 && (
                        <span className="notification-badge">
                          {totalCount > 99 ? "99+" : totalCount}
                        </span>
                      )} */}
                    </div>
                  </DropdownToggle>

                  <DropdownMenu
                    right
                    className="notification-dropdown-menu"
                  >
                   

                    <div className="notification-dropdown-list">
                      {isNotifLoading ? (
                        <div className="notification-empty-state">
                          <i className="fa fa-spinner fa-spin mr-2" />
                          Loading...
                        </div>
                      ) : actionRequired.length > 0 ? (
                        actionRequired.map((item, idx) => (
                          <DropdownItem
                            key={idx}
                            className="notification-item"
                            onClick={goToActionRequired}
                          >
                 
                            <div className="notification-item-text">
                              <div className="notification-item-title">
                                {item.message}
                              </div>
                            
                            </div>
                            <div className="notification-item-count">
                              {item.count}
                            </div>
                          </DropdownItem>
                        ))
                      ) : (
                        <DropdownItem
                          className="notification-item is-empty"
                          disabled
                        >
                        
                          <div className="notification-item-text">
                          
                            <div className="notification-item-desc">
                              No pending actions at the moment
                            </div>
                          </div>
                        </DropdownItem>
                      )}
                    </div>
                  </DropdownMenu>
                </Dropdown>
              )}

              <UncontrolledDropdown nav>
                <DropdownToggle className="nav-link pr-0" color="" tag="a">
                  <Media className="align-items-center">
                    <Media className="ml-2 d-flex d-lg-block align-items-center mt--3">
                      <span
                        className="mb-0 text-medium font-weight-bold"
                        style={{ cursor: "pointer" }}
                      >
                        {userName?.name === ""
                          ? userName?.Email
                          : userName?.name}
                      </span>
                      <span
                        className="btn-inner--icon ml-2"
                        style={{ cursor: "pointer" }}
                      >
                        <i className="fa fa-ellipsis-v" />
                      </span>
                    </Media>
                  </Media>
                </DropdownToggle>
                <DropdownMenu right>
                  <DropdownItem onClick={() => logout()}>
                    <i className="fa fa-sign-out" style={{ color: "gray" }} />
                    <span style={{ color: "gray" }}>Logout</span>
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
            </Nav>
          </Collapse>
        </Container>
      </Navbar>
    </>
  );
}

AdminNavbar.defaultProps = {
  toggleSidenav: () => {},
  sidenavOpen: false,
  theme: "dark",
};
AdminNavbar.propTypes = {
  toggleSidenav: PropTypes.func,
  sidenavOpen: PropTypes.bool,
  theme: PropTypes.oneOf(["dark", "light"]),
};

export default AdminNavbar;